import { Settings, Loader2 } from 'lucide-react';
import {
  ProfileCard,
  ProfileForm,
  AccountInfo,
  EmailSettingsCard,
  EmailPreferencesCard,
  ClickupSyncCard,
  PasswordChangeCard,
} from '@/features/settings';
import { useAuth } from '@/hooks/use-auth';
import { useMyCapabilities } from '@/hooks/use-permissions';
import { Separator } from '@/components/ui/separator';
import { APP_VERSION } from '@/lib/version';

export function SettingsPage() {
  const { user, isLoading } = useAuth();
  const { data: capabilities } = useMyCapabilities();
  const canManageSmtp = Boolean(capabilities?.['settings.smtp.manage']);
  const canManageClickupSync = Boolean(capabilities?.['permissions.manage']);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <Settings className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">Unable to load profile</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div>
        <div className="text-xs text-white/70 mb-2">Version: {APP_VERSION}</div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account and profile</p>
      </div>

      <ProfileCard user={user} />

      <ProfileForm user={user} />

      <PasswordChangeCard />

      <AccountInfo user={user} />

      <Separator className="my-8" />

      <div>
        <h2 className="text-xl font-semibold mb-1">Notifications</h2>
        <p className="text-muted-foreground text-sm mb-6">
          Manage your notification preferences
        </p>

        <EmailPreferencesCard />
      </div>

      {(canManageSmtp || canManageClickupSync) && (
        <>
          <Separator className="my-8" />

          <div>
            <h2 className="text-xl font-semibold mb-1">System Settings</h2>
            <p className="text-muted-foreground text-sm mb-6">
              Configure system-wide settings (Admin only)
            </p>

            <div className="space-y-6">
              {canManageSmtp && <EmailSettingsCard />}
              {canManageClickupSync && <ClickupSyncCard />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
